import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode, getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";

export function isNodeLockedForEditing(document: CurvaDocument, nodeId: string): boolean {
  const lineage = findNodeLineage(document, nodeId);
  return lineage.some(node => node.locked);
}

export function editableNodeIds(document: CurvaDocument, nodeIds: string[]): string[] {
  return [...new Set(nodeIds)].filter(nodeId => !isNodeLockedForEditing(document, nodeId));
}

export function isNodeDescendantOf(document: CurvaDocument, nodeId: string, ancestorId: string): boolean {
  const lineage = findNodeLineage(document, nodeId);
  return lineage.slice(0, -1).some(node => node.id === ancestorId);
}

export function isNodeInsideComponentInstance(document: CurvaDocument, nodeId: string): boolean {
  return findNodeLineage(document, nodeId).some(node => node.type === "instance");
}

export function isComponentInstanceDropParent(document: CurvaDocument, parentId: string): boolean {
  if (document.pages.some(page => page.id === parentId)) {
    return false;
  }

  const parent = findSceneNode(document, parentId);
  if (!parent) {
    return true;
  }

  if (parent.type === "instance") {
    return true;
  }

  return isNodeInsideComponentInstance(document, parentId);
}

function findNodeLineage(document: CurvaDocument, nodeId: string): SceneNode[] {
  for (const page of document.pages) {
    const lineage = findNodeLineageInNodes(page.children, nodeId);
    if (lineage.length > 0) {
      return lineage;
    }
  }

  return [];
}

function findNodeLineageInNodes(nodes: SceneNode[], nodeId: string, ancestors: SceneNode[] = []): SceneNode[] {
  for (const node of nodes) {
    const lineage = [...ancestors, node];
    if (node.id === nodeId) {
      return lineage;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const childLineage = findNodeLineageInNodes(children, nodeId, lineage);
    if (childLineage.length > 0) {
      return childLineage;
    }
  }

  return [];
}
